import React, { useState, useCallback, useMemo } from "react";
import { AppStoreContext, type AppStore } from "./useAppStore";
import type { ScanLog, SavedContact, User } from "../types";
import { storage, KEYS } from "../services/storage";
import {
  restoreCameraGrants,
  snapshotCameraGrants,
} from "../services/cameraPermission";
import { defaultUser } from "../state/defaultUser";
import type { EventRolePayload } from "../services/visitorSessionService";

export function AppStoreProvider({ children }: { children: React.ReactNode }) {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(
    () => storage.get<string | null>(KEYS.SELECTED_EVENT, null),
  );
  const [onboardingComplete, setOnboardingComplete] = useState<boolean>(
    () => storage.get<boolean>(KEYS.ONBOARDING_DONE, false),
  );
  const [scanHistory, setScanHistory] = useState<ScanLog[]>(
    () => storage.get<ScanLog[]>(KEYS.SCAN_HISTORY, []),
  );
  const [savedContacts, setSavedContacts] = useState<SavedContact[]>(
    () => storage.get<SavedContact[]>(KEYS.SAVED_CONTACTS, []),
  );
  const [user, setUser] = useState<User>(
    () => storage.get<User>(KEYS.USER_PROFILE, defaultUser),
  );
  const [eventRole, setEventRoleState] = useState<User["role"] | null>(null);
  const [exhibitorCompanyId, setExhibitorCompanyId] = useState<string | null>(null);
  const [exhibitorCompanyName, setExhibitorCompanyName] = useState<string | null>(null);
  const [exhibitorBooth, setExhibitorBooth] = useState<string | null>(null);
  const [exhibitorBoothQrCode, setExhibitorBoothQrCode] = useState<string | null>(null);
  const [exhibitorCompanyQrCode, setExhibitorCompanyQrCode] = useState<string | null>(null);
  const [exhibitorRepresentativeId, setExhibitorRepresentativeId] = useState<string | null>(null);
  const [exhibitorBadgeQrCode, setExhibitorBadgeQrCode] = useState<string | null>(null);
  const [navbarVisible, setNavbarVisible] = useState<boolean>(true);

  const setEventRole = useCallback((role: EventRolePayload | null) => {
    if (!role) {
      setEventRoleState(null);
      setExhibitorCompanyId(null);
      setExhibitorCompanyName(null);
      setExhibitorBooth(null);
      setExhibitorBoothQrCode(null);
      setExhibitorCompanyQrCode(null);
      setExhibitorRepresentativeId(null);
      setExhibitorBadgeQrCode(null);
      return;
    }
    setEventRoleState(role.eventRole);
    setExhibitorCompanyId(role.companyId ?? null);
    setExhibitorCompanyName(role.companyName ?? null);
    setExhibitorBooth(role.booth ?? null);
    setExhibitorBoothQrCode(role.boothQrCode ?? null);
    setExhibitorCompanyQrCode(role.companyQrCode ?? null);
    setExhibitorRepresentativeId(role.representativeId ?? null);
    setExhibitorBadgeQrCode(role.badgeQrCode ?? null);
    setUser((prev) => {
      const next: User = {
        ...prev,
        role: role.eventRole,
        companyName: role.companyName ?? prev.companyName,
        fullName:
          role.fullName &&
          !["Посетитель", "MAX User", "Представитель"].includes(role.fullName)
            ? role.fullName
            : prev.fullName,
        position: role.position || prev.position,
      };
      storage.set(KEYS.USER_PROFILE, next);
      return next;
    });
  }, []);

  const selectEvent = useCallback(
    (eventId: string) => {
      setSelectedEventId(eventId);
      storage.set(KEYS.SELECTED_EVENT, eventId);
      void import("../services/supportService").then(({ supportService }) => {
        supportService.syncContext(eventId);
      });
      void import("../services/visitorSessionService").then(({ visitorSessionService }) => {
        visitorSessionService.syncEventRole(eventId, setEventRole).catch(() => undefined);
      });
    },
    [setEventRole],
  );

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(true);
    storage.set(KEYS.ONBOARDING_DONE, true);
  }, []);

  const addScan = useCallback((scan: ScanLog) => {
    setScanHistory((prev) => {
      const idx = prev.findIndex((s) => s.id === scan.id);
      if (idx >= 0) {
        // Merge notes/tags from server when reloading history / re-scan.
        const next = [...prev];
        next[idx] = {
          ...next[idx]!,
          ...scan,
          notes: scan.notes !== undefined ? scan.notes : next[idx]!.notes,
          tags: scan.tags !== undefined ? scan.tags : next[idx]!.tags,
        };
        storage.set(KEYS.SCAN_HISTORY, next);
        return next;
      }
      const next = [scan, ...prev];
      storage.set(KEYS.SCAN_HISTORY, next);
      return next;
    });
  }, []);

  const updateScan = useCallback(
    (scanId: string, patch: Pick<ScanLog, "notes" | "tags">) => {
      setScanHistory((prev) => {
        const idx = prev.findIndex((s) => s.id === scanId);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = {
          ...next[idx]!,
          notes: patch.notes !== undefined ? patch.notes : next[idx]!.notes,
          tags: patch.tags !== undefined ? patch.tags : next[idx]!.tags,
        };
        storage.set(KEYS.SCAN_HISTORY, next);
        return next;
      });
    },
    [],
  );

  const saveContact = useCallback((contact: SavedContact) => {
    setSavedContacts((prev) => {
      const idx = prev.findIndex((c) => c.companyId === contact.companyId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          ...contact,
          notes: contact.notes ?? next[idx].notes,
          tags: contact.tags ?? next[idx].tags,
        };
        storage.set(KEYS.SAVED_CONTACTS, next);
        return next;
      }
      const next = [contact, ...prev];
      storage.set(KEYS.SAVED_CONTACTS, next);
      return next;
    });
  }, []);

  const removeSavedContact = useCallback((companyId: string, eventId?: string) => {
    setSavedContacts((prev) => {
      const next = prev.filter(
        (c) => !(c.companyId === companyId && (!eventId || c.eventId === eventId)),
      );
      storage.set(KEYS.SAVED_CONTACTS, next);
      return next;
    });
  }, []);

  const updateSavedContact = useCallback(
    (
      companyId: string,
      patch: Pick<SavedContact, "notes" | "tags"> &
        Partial<Pick<SavedContact, "eventId" | "userId">>,
    ) => {
      setSavedContacts((prev) => {
        const idx = prev.findIndex((c) => c.companyId === companyId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            notes: patch.notes !== undefined ? patch.notes : next[idx].notes,
            tags: patch.tags !== undefined ? patch.tags : next[idx].tags,
          };
          storage.set(KEYS.SAVED_CONTACTS, next);
          return next;
        }
        const created: SavedContact = {
          id: `sc-${Date.now().toString(36)}`,
          userId: patch.userId ?? "local",
          eventId: patch.eventId ?? "",
          companyId,
          savedAt: new Date().toISOString(),
          notes: patch.notes,
          tags: patch.tags,
        };
        const next = [created, ...prev];
        storage.set(KEYS.SAVED_CONTACTS, next);
        return next;
      });
    },
    [],
  );

  const isContactUnlocked = useCallback(
    (companyId: string) => scanHistory.some((s) => s.companyId === companyId),
    [scanHistory],
  );

  const isRepresentativeUnlocked = useCallback(
    (representativeId: string) =>
      scanHistory.some(
        (s) =>
          s.representativeId === representativeId ||
          (s.scanTargetType === "representative" && s.scanTargetId === representativeId),
      ),
    [scanHistory],
  );

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => {
      const next = { ...prev, ...partial };
      storage.set(KEYS.USER_PROFILE, next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    const cameraSnap = snapshotCameraGrants();
    storage.clear();
    restoreCameraGrants(cameraSnap);
    setSelectedEventId(null);
    setOnboardingComplete(false);
    setScanHistory([]);
    setSavedContacts([]);
    setUser(defaultUser);
    setEventRoleState(null);
    setExhibitorCompanyId(null);
    setExhibitorCompanyName(null);
    setExhibitorBooth(null);
    setExhibitorBoothQrCode(null);
    setExhibitorCompanyQrCode(null);
    setExhibitorRepresentativeId(null);
    setExhibitorBadgeQrCode(null);
  }, []);

  const store = useMemo<AppStore>(
    () => ({
      selectedEventId,
      onboardingComplete,
      scanHistory,
      savedContacts,
      user,
      eventRole,
      exhibitorCompanyId,
      exhibitorCompanyName,
      exhibitorBooth,
      exhibitorBoothQrCode,
      exhibitorCompanyQrCode,
      exhibitorRepresentativeId,
      exhibitorBadgeQrCode,
      navbarVisible,
      selectEvent,
      completeOnboarding,
      addScan,
      updateScan,
      saveContact,
      removeSavedContact,
      updateSavedContact,
      isContactUnlocked,
      isRepresentativeUnlocked,
      updateUser,
      setEventRole,
      reset,
      setNavbarVisible,
    }),
    [
      selectedEventId,
      onboardingComplete,
      scanHistory,
      savedContacts,
      user,
      eventRole,
      exhibitorCompanyId,
      exhibitorCompanyName,
      exhibitorBooth,
      exhibitorBoothQrCode,
      exhibitorCompanyQrCode,
      exhibitorRepresentativeId,
      exhibitorBadgeQrCode,
      navbarVisible,
      selectEvent,
      completeOnboarding,
      addScan,
      updateScan,
      saveContact,
      removeSavedContact,
      updateSavedContact,
      isContactUnlocked,
      isRepresentativeUnlocked,
      updateUser,
      setEventRole,
      reset,
    ],
  );

  return (
    <AppStoreContext.Provider value={store}>{children}</AppStoreContext.Provider>
  );
}
